/**
 * 1·2회 통합본(이번 회차 한정) 안내 배너 (요약 PDF 상품 상세, 이론/코딩 공용).
 *
 * 26년 1회 출제 경향 변화로 적중률이 아쉬웠던 만큼, 이번에 한해 1·2회를 묶은 통합본으로
 * 제공하면서, "구매 전 꼭 알아야 할 공지"를 상품 설명 최상단에 풀폭 강조 배너로 노출한다.
 * 이론(26-1-compressed)·코딩(26-1-coding-compressed) 두 상세가 같은 소스를 소비한다(drift 방지).
 *
 * ## variant
 * - `theory`/`coding`: **마지막 보강 포인트 불릿 한 줄만** 분기. 제목·공통 본문·디자인은 동일.
 *   (boolean prop 누적이 아니라 데이터 한 줄 분기라 단일 컴포넌트 + variant 로 충분 — compound
 *   component 리팩토링은 과함.)
 *
 * ## full 1종 (컴팩트/사이드바 없음)
 * Complete 업셀(CompleteUpsellBanner)과 달리 이 안내는 좌측 사이드바에 쌓이지 않고 **본문 상품
 * 설명 최상단**에만 풀폭으로 1번 노출되므로 컴팩트/사이드바 버전이 불필요하다. props 단순 유지.
 *
 * ## 톤 선택 (sky) — 같은 페이지 형제 톤과 충돌 회피
 * 상품 상세 페이지엔 이미 purple(적중 실적)·amber(Complete/리뷰)·blue·indigo(코딩 크로스셀)·
 * orange(perks)·blue(contents)·green(features)·gray(download) 톤이 쓰인다. 공지/전환 안내는
 * 이들과 시맨틱·색조가 구분돼야 해서 **sky(하늘색)** 를 택했다. sky 는 amber/purple/orange/green/
 * gray 와 겹치지 않고, blue/indigo 보다 밝고 cyan 쪽이라 색조가 분리된다(공지 notice 시맨틱).
 *
 * ## 연한 틴트 카드 패밀리 (dark-mode-colors.md §4-4-1 회귀 결론)
 * 솔리드/그라데이션 + 흰색 작은 글씨로 가지 않는다(WCAG 대비 회귀 방지). CompleteUpsellBanner 와
 * 동일하게 **연한 배경 위 진한 동계열 글씨**로 가시성 유지:
 * - 표면 `bg-sky-50 dark:bg-sky-950/40`, 보더 `border-sky-300 dark:border-sky-800`
 *   (공지라 형제 -200 보다 한 단계 진한 -300 으로 살짝 강조, Complete 와 같은 강조 레벨).
 * - 헤딩 `text-sky-900 dark:text-sky-200`, eyebrow `text-sky-700 dark:text-sky-400`,
 *   불릿 본문 `text-gray-800 dark:text-gray-200`, 강조 `text-sky-800 dark:text-sky-300`,
 *   체크/불릿 마커 `text-sky-600 dark:text-sky-400`(§2 페어). 모든 색 light/dark 페어 명시.
 *
 * ## a11y
 * 헤딩은 본문 "상품 설명" h2 의 하위 안내이므로 `<h3>`(위계 유지). 장식 체크마크는 `aria-hidden`.
 * 강조는 `<strong>` 로 의미 마크업.
 */

import { cn } from "@/lib/utils";

type PdfIntegrationNoticeVariant = "theory" | "coding";

/** variant 무관 공통 안내 불릿. */
const COMMON_POINTS: readonly React.ReactNode[] = [
  <>
    이번 26년 1회는 출제 경향이 크게 바뀌어 적중률이 아쉬웠던 회차였습니다.{" "}
    <strong className="font-bold text-sky-800 dark:text-sky-300">
      1회에 응시하신 분들의 2회 재도전
    </strong>
    에 도움이 되도록, 이번에 한해{" "}
    <strong className="font-bold text-sky-800 dark:text-sky-300">
      1·2회를 합친 통합본
    </strong>
    으로 제공합니다.
  </>,
  <>
    지금 구매하셔도{" "}
    <strong className="font-bold text-sky-800 dark:text-sky-300">
      1·2회 통합 v2-1 최신본을 바로
    </strong>{" "}
    받아보실 수 있어요. 기존 구매자도 구매 목록에서 재구매 없이 최신 파일을 받을 수 있습니다.
  </>,
  <>
    가림막(Q)·가로(H){" "}
    <strong className="font-bold text-sky-800 dark:text-sky-300">
      v2-1 버전
    </strong>
    {"도 함께 제공됩니다."}
  </>,
];

/** variant 별 마지막 보강 포인트 불릿. */
const VARIANT_POINT: Record<PdfIntegrationNoticeVariant, React.ReactNode> = {
  theory: (
    <>
      이번 회차에 출제 비중이 커진{" "}
      <strong className="font-bold text-sky-800 dark:text-sky-300">
        보안 파트를 전 범위로 넓혀
      </strong>{" "}
      보강했습니다.
    </>
  ),
  coding: (
    <>
      파이썬 신유형(얕은 복사 등)과{" "}
      <strong className="font-bold text-sky-800 dark:text-sky-300">
        SQL을 기초부터 더 깊게
      </strong>{" "}
      보강했습니다.
    </>
  ),
};

interface PdfIntegrationNoticeProps {
  variant: PdfIntegrationNoticeVariant;
  /** 추가 className(레이아웃 보정용, 선택). */
  className?: string;
}

export default function PdfIntegrationNotice({
  variant,
  className,
}: PdfIntegrationNoticeProps) {
  const points: React.ReactNode[] = [...COMMON_POINTS, VARIANT_POINT[variant]];

  return (
    <div
      className={cn(
        // 연한 sky 틴트 카드 — 공지/전환 안내. 솔리드/그라데이션·흰 작은 글씨로 가지 않는다
        // (dark-mode-colors.md §4-4-1). 모든 색 light/dark 페어 명시.
        "break-words rounded-2xl border bg-sky-50 p-6",
        "border-sky-300 dark:border-sky-800 dark:bg-sky-950/40",
        className,
      )}
    >
      {/* 헤더 — 연한 배경 위 진한 sky 글씨(가시성 유지). 상품 설명 h2 하위라 h3. */}
      <p className="text-xs font-bold uppercase tracking-wide text-sky-700 dark:text-sky-400">
        구매 전 안내
      </p>
      <h3 className="mt-0.5 text-xl font-extrabold leading-tight text-sky-900 dark:text-sky-200">
        📢 이번엔 1·2회 통합본으로 제공합니다
      </h3>

      {/* 안내 불릿 — 진한 글씨(연한 배경 위 고대비) + sky 체크마크. */}
      <ul className="mt-4 space-y-2 text-sm leading-relaxed text-gray-800 dark:text-gray-200">
        {points.map((point, index) => (
          <li key={index} className="flex items-start gap-2">
            <span
              aria-hidden="true"
              className="mt-0.5 shrink-0 font-bold leading-snug text-sky-600 dark:text-sky-400"
            >
              &#10003;
            </span>
            <span className="leading-snug">{point}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
